import { useState, useEffect, useMemo } from 'react'
import { Users, Clock, Phone, FileText, CheckSquare, Search } from 'lucide-react'
import { useFilters } from '../contexts/FilterContext'
import { api } from '../services/api'
import styles from './Leads.module.css'

interface KanbanLead {
  id: number
  nome: string
  telefone?: string
  email?: string
  corretor: string
  empreendimento?: string
  data: string
  temperatura: 'hot' | 'warm' | 'cold'
  dias_sem_contato?: number
}

interface KanbanColumn {
  id: string
  title: string
  subtitle: string
  color: string
  situacoes: string[]
}

const KANBAN_COLUMNS: KanbanColumn[] = [
  {
    id: 'sv-nova',
    title: 'Nova Conversao',
    subtitle: 'SV - Sem Vendedor',
    color: '#6366f1',
    situacoes: ['SV - Nova Conversão', 'SV - Especial - Fin. Direto'],
  },
  {
    id: 'av-aguardando',
    title: 'Aguardando',
    subtitle: 'AV - Na Fila',
    color: '#8b5cf6',
    situacoes: ['AV - Aguardando Atendimento'],
  },
  {
    id: 'av-abordagem',
    title: 'Abordagem',
    subtitle: 'Tentativa de Contato',
    color: '#ec4899',
    situacoes: ['AV - Abordagem - Tentativa de Contato'],
  },
  {
    id: 'av-atendimento',
    title: 'Em Atendimento',
    subtitle: 'Corretor Trabalhando',
    color: '#f59e0b',
    situacoes: ['AV - Em Atendimento'],
  },
  {
    id: 'av-retorno',
    title: 'Retorno Futuro',
    subtitle: 'Aguardando Retorno',
    color: '#a855f7',
    situacoes: ['AV - Retorno Futuro'],
  },
  {
    id: 'av-docs',
    title: 'Aguardando Docs',
    subtitle: 'Documentacao Pendente',
    color: '#14b8a6',
    situacoes: ['AV - Aguardando Docs - I', 'AV - Aguardando Docs - X'],
  },
  {
    id: 'pre-cadastro',
    title: 'Pre Cadastro',
    subtitle: 'Analise de Credito',
    color: '#22c55e',
    situacoes: ['Em Pré Cadastro'],
  },
  {
    id: 'com-reserva',
    title: 'Com Reserva',
    subtitle: 'Unidade Bloqueada',
    color: '#10b981',
    situacoes: ['Com Reserva'],
  },
  {
    id: 'venda-finalizada',
    title: 'Venda Finalizada',
    subtitle: 'Processo Concluido',
    color: '#059669',
    situacoes: ['Venda Finalizada'],
  },
]

interface KanbanApiResponse {
  colunas: Array<{
    id: string
    title: string
    subtitle: string
    count: number
    leads: Array<{
      id: number
      nome: string
      telefone: string
      corretor: string
      empreendimento: string
      temperatura: string
      dias_sem_contato: number
      origem: string
    }>
  }>
  total: number
}

export function Leads() {
  const { filters, filtersVersion, filterOptions } = useFilters()
  const [kanbanData, setKanbanData] = useState<KanbanApiResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filtros locais da pagina (para override ou filtro adicional)
  const [localFilterImobiliaria, setLocalFilterImobiliaria] = useState<string>('')
  const [localFilterCorretor, setLocalFilterCorretor] = useState<string>('')
  const [filterSituacao, setFilterSituacao] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState('')

  // Combina filtros globais com filtros locais (local tem prioridade se definido)
  const effectiveImobiliaria = localFilterImobiliaria || (filters.idImobiliaria ? String(filters.idImobiliaria) : '')
  const effectiveCorretor = localFilterCorretor || (filters.idCorretor ? String(filters.idCorretor) : '')

  // Filtra corretores pela imobiliária selecionada
  const corretoresFiltrados = effectiveImobiliaria
    ? filterOptions.corretoresCompletos.filter(
        (c) => c.idImobiliaria === Number(effectiveImobiliaria)
      )
    : filterOptions.corretoresCompletos

  // Limpa corretor selecionado quando muda a imobiliária (se corretor não pertence à nova imobiliária)
  useEffect(() => {
    if (effectiveImobiliaria && effectiveCorretor) {
      const corretorPertence = corretoresFiltrados.some(
        (c) => String(c.value) === effectiveCorretor
      )
      if (!corretorPertence) {
        setLocalFilterCorretor('')
      }
    }
  }, [effectiveImobiliaria])

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)

      try {
        // Busca dados do kanban respeitando TODOS os filtros globais + locais
        const kanban = await api.getLeadsKanban({
          dataInicio: filters.dataInicio,
          dataFim: filters.dataFim,
          tipoNegocio: filters.tipoNegocio,
          idEmpreendimento: filters.idEmpreendimento || undefined,
          idImobiliaria: effectiveImobiliaria ? Number(effectiveImobiliaria) : undefined,
          idCorretor: effectiveCorretor ? Number(effectiveCorretor) : undefined,
          busca: searchTerm || undefined,
        })
        setKanbanData(kanban)
      } catch (err) {
        console.error('Erro ao carregar leads:', err)
        setError('Erro ao carregar dados de leads')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [filtersVersion, filters, effectiveImobiliaria, effectiveCorretor, searchTerm])

  // Mapeia IDs das colunas do frontend para os IDs da API
  const columnMapping: Record<string, string> = {
    'sv-nova': 'nova-conversao',
    'av-aguardando': 'aguardando',
    'av-abordagem': 'abordagem',
    'av-atendimento': 'em-atendimento',
    'av-retorno': 'retorno-futuro',
    'av-docs': 'aguardando-docs',
    'pre-cadastro': 'pre-cadastro',
    'com-reserva': 'com-reserva',
    'venda-finalizada': 'venda-finalizada',
  }

  // Usa contagens reais do banco de dados (vindas da API)
  const kanbanCounts = useMemo(() => {
    const counts: Record<string, number> = {}

    if (kanbanData?.colunas) {
      KANBAN_COLUMNS.forEach((column) => {
        const apiColumnId = columnMapping[column.id]
        const apiColumn = kanbanData.colunas.find(c => c.id === apiColumnId)
        counts[column.id] = apiColumn?.count || 0
      })
    } else {
      KANBAN_COLUMNS.forEach((column) => {
        counts[column.id] = 0
      })
    }

    return counts
  }, [kanbanData])

  // KPIs calculados usando dados reais do Kanban
  const kpis = useMemo(() => {
    const totalAtivos = kanbanData?.total || 0

    const aguardando = (kanbanCounts['sv-nova'] || 0) + (kanbanCounts['av-aguardando'] || 0)
    const emAtendimento = (kanbanCounts['av-abordagem'] || 0) + (kanbanCounts['av-atendimento'] || 0) + (kanbanCounts['av-retorno'] || 0) + (kanbanCounts['av-docs'] || 0)
    const preCadastro = kanbanCounts['pre-cadastro'] || 0
    const comReserva = (kanbanCounts['com-reserva'] || 0) + (kanbanCounts['venda-finalizada'] || 0)

    return { totalAtivos, aguardando, emAtendimento, preCadastro, comReserva }
  }, [kanbanData, kanbanCounts])

  // Busca leads reais para uma coluna do kanban
  const getLeadsForColumn = (columnId: string): KanbanLead[] => {
    if (!kanbanData?.colunas) return []

    const apiColumnId = columnMapping[columnId]
    const apiColumn = kanbanData.colunas.find(c => c.id === apiColumnId)

    if (!apiColumn?.leads) return []

    return apiColumn.leads.map(lead => ({
      id: lead.id,
      nome: lead.nome,
      telefone: lead.telefone ? `****-${lead.telefone.slice(-4)}` : undefined,
      email: undefined,
      corretor: lead.corretor,
      empreendimento: lead.empreendimento,
      data: lead.data || '-',
      temperatura: (lead.temperatura === 'quente' ? 'hot' : lead.temperatura === 'morno' ? 'warm' : 'cold') as 'hot' | 'warm' | 'cold',
      dias_sem_contato: lead.dias_sem_contato,
    }))
  }

  const getTemperaturaClass = (temp: 'hot' | 'warm' | 'cold') => styles[temp]

  const getTemperaturaLabel = (temp: 'hot' | 'warm' | 'cold') => {
    const labels = { hot: 'Quente', warm: 'Morno', cold: 'Frio' }
    return labels[temp]
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>Leads / Kanban</h1>
            <p className={styles.pageSubtitle}>Gestao e acompanhamento de leads por situacao</p>
          </div>
        </div>
        <div className={styles.errorState}>
          <p>{error}</p>
          <p>Verifique se a API esta funcionando corretamente.</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Leads / Kanban</h1>
          <p className={styles.pageSubtitle}>Gestao e acompanhamento de leads por situacao</p>
        </div>
      </div>

      {/* Filtros Especificos da Pagina */}
      <section className={styles.section}>
        <div className={styles.leadsFilterBar}>
          <div className={styles.leadsFilterGroup}>
            <label className={styles.leadsFilterLabel}>Imobiliaria</label>
            <select
              className={styles.filterSelect}
              value={effectiveImobiliaria}
              onChange={(e) => setLocalFilterImobiliaria(e.target.value)}
            >
              <option value="">Todas</option>
              <optgroup label="Internas">
                <option value="109">UTANI</option>
                <option value="4">SDR</option>
              </optgroup>
              <optgroup label="Parceiras">
                {filterOptions.imobiliarias.map((imob) => (
                  <option key={imob.value} value={imob.value}>
                    {imob.label}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>

          <div className={styles.leadsFilterGroup}>
            <label className={styles.leadsFilterLabel}>Corretor</label>
            <select
              className={styles.filterSelect}
              value={effectiveCorretor}
              onChange={(e) => setLocalFilterCorretor(e.target.value)}
            >
              <option value="">{effectiveImobiliaria ? 'Corretores da Imobiliária' : 'Todos'}</option>
              {corretoresFiltrados.map((corr) => (
                <option key={corr.value} value={corr.value}>
                  {corr.label}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.leadsFilterGroup}>
            <label className={styles.leadsFilterLabel}>Situacao</label>
            <select
              className={styles.filterSelect}
              value={filterSituacao}
              onChange={(e) => setFilterSituacao(e.target.value)}
            >
              <option value="">Todas</option>
              <option value="sv-nova">SV - Nova Conversao</option>
              <option value="av-aguardando">AV - Aguardando Atendimento</option>
              <option value="av-abordagem">AV - Tentativa de Contato</option>
              <option value="av-atendimento">AV - Em Atendimento</option>
              <option value="av-retorno">AV - Retorno Futuro</option>
              <option value="av-docs">AV - Aguardando Docs</option>
              <option value="pre-cadastro">Em Pre Cadastro</option>
              <option value="com-reserva">Com Reserva</option>
              <option value="venda-finalizada">Venda Finalizada</option>
            </select>
          </div>

          <div className={styles.leadsSearchContainer}>
            <Search size={16} />
            <input
              type="text"
              placeholder="Buscar lead por nome, telefone..."
              className={styles.leadsSearchInput}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* KPIs */}
      <section className={styles.section}>
        <div className={styles.kpiGrid5}>
          <div className={`${styles.kpiCard} ${styles.kpiCardSm}`}>
            <div className={`${styles.kpiIcon} ${styles.primary}`}>
              <Users size={18} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Total Ativos</span>
              <span className={styles.kpiValue}>{loading ? '--' : kpis.totalAtivos.toLocaleString('pt-BR')}</span>
            </div>
          </div>

          <div className={`${styles.kpiCard} ${styles.kpiCardSm}`}>
            <div className={`${styles.kpiIcon} ${styles.info}`}>
              <Clock size={18} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Aguardando</span>
              <span className={styles.kpiValue}>{loading ? '--' : kpis.aguardando.toLocaleString('pt-BR')}</span>
            </div>
          </div>

          <div className={`${styles.kpiCard} ${styles.kpiCardSm}`}>
            <div className={`${styles.kpiIcon} ${styles.secondary}`}>
              <Phone size={18} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Em Atendimento</span>
              <span className={styles.kpiValue}>{loading ? '--' : kpis.emAtendimento.toLocaleString('pt-BR')}</span>
            </div>
          </div>

          <div className={`${styles.kpiCard} ${styles.kpiCardSm}`}>
            <div className={`${styles.kpiIcon} ${styles.warning}`}>
              <FileText size={18} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Pre Cadastro</span>
              <span className={styles.kpiValue}>{loading ? '--' : kpis.preCadastro.toLocaleString('pt-BR')}</span>
            </div>
          </div>

          <div className={`${styles.kpiCard} ${styles.kpiCardSm}`}>
            <div className={`${styles.kpiIcon} ${styles.success}`}>
              <CheckSquare size={18} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Com Reserva</span>
              <span className={styles.kpiValue}>{loading ? '--' : kpis.comReserva.toLocaleString('pt-BR')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Kanban Board com 7 Colunas */}
      <section className={styles.section}>
        <div className={styles.kanbanBoard}>
          {KANBAN_COLUMNS.filter((column) => !filterSituacao || column.id === filterSituacao).map((column) => (
            <div key={column.id} className={styles.kanbanColumn}>
              <div
                className={styles.kanbanColumnHeader}
                style={{ '--column-color': column.color } as React.CSSProperties}
              >
                <div className={styles.kanbanColumnInfo}>
                  <span className={styles.kanbanColumnTitle}>{column.title}</span>
                  <span className={styles.kanbanColumnSubtitle}>{column.subtitle}</span>
                </div>
                <span className={styles.kanbanColumnCount}>
                  {loading ? '--' : kanbanCounts[column.id] || 0}
                </span>
              </div>
              <div className={styles.kanbanColumnContent}>
                {loading ? (
                  <div className={styles.loadingState}>Carregando...</div>
                ) : (
                  getLeadsForColumn(column.id).map((lead) => (
                    <div
                      key={lead.id}
                      className={`${styles.kanbanCard} ${getTemperaturaClass(lead.temperatura)}`}
                    >
                      <div className={styles.kanbanCardHeader}>
                        <span className={styles.kanbanCardName}>{lead.nome}</span>
                        <span className={`${styles.kanbanCardBadge} ${getTemperaturaClass(lead.temperatura)}`}>
                          {getTemperaturaLabel(lead.temperatura)}
                        </span>
                      </div>
                      <div className={styles.kanbanCardInfo}>
                        {lead.telefone && <span>{lead.telefone}</span>}
                        {lead.dias_sem_contato !== undefined && lead.dias_sem_contato > 0 && (
                          <span className={styles.kanbanCardDias}>
                            {lead.dias_sem_contato} dia{lead.dias_sem_contato > 1 ? 's' : ''} sem contato
                          </span>
                        )}
                      </div>
                      <div className={styles.kanbanCardFooter}>
                        <span className={styles.kanbanCardCorretor}>{lead.corretor}</span>
                        <span className={styles.kanbanCardDate}>{lead.data}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
